import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TableSortLabel,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import PestControlIcon from '@mui/icons-material/PestControl';
import ThumbDown from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUp from '@mui/icons-material/ThumbUpAltOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LanguageIcon from '@mui/icons-material/Language';
import FieldsPicker from '../chatbot/FieldsPicker';
import ColumnVisibilityPicker from '../chatbot/ColumnVisibilityPicker';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentFailureReportTitle,
  selectCurrentFailureReportSubtitle,
  selectNlgParameterTransmissionResult,
  selectIsNlgParameterTransmissionLoading
} from '../../store/features/transmissionSlice';

type Order = 'asc' | 'desc';

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any) => string;
  numeric?: boolean;
  isDate?: boolean;
}

interface TransmissionTableProps {
  columns: Column[];
  data: any[];
  title?: string;
  subtitle?: string;
  visibleColumns?: string[];
  onVisibleColumnsChange?: (visibleColumns: string[]) => void;
}

const TransmissionTable: React.FC<TransmissionTableProps> = ({
  columns,
  data,
  visibleColumns: propVisibleColumns,
  onVisibleColumnsChange
}) => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('');
  const [displayType, setDisplayType] = useState<'table' | 'chart'>('table');
  const [axes, setAxes] = useState({ x: '', y: '' });
  const [openFieldPicker, setOpenFieldPicker] = useState(false);
  const [openColumnPicker, setOpenColumnPicker] = useState(false);
  const [internalVisibleColumns, setInternalVisibleColumns] = useState<string[]>(columns.map(col => col.id));
  const [columnPickerAnchorEl, setColumnPickerAnchorEl] = useState<HTMLElement | null>(null);
  const [clickedTable, setClickedTable] = useState('bugs_table');
  const [clickedThumb, setClickedThumb] = useState('');
  const [selectedSerialNumbers, setSelectedSerialNumbers] = useState<string[]>([]);
  const title = useSelector(selectCurrentFailureReportTitle);
  const subtitle = useSelector(selectCurrentFailureReportSubtitle);
  const nlgParameterTransmissionResult = useSelector(selectNlgParameterTransmissionResult)
  const isNlgParameterTransmissionLoading = useSelector(selectIsNlgParameterTransmissionLoading)
  // Use prop visible columns if provided, otherwise use internal state
  const visibleColumns = propVisibleColumns || internalVisibleColumns;

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleShowPicker = () => {
    if (displayType === 'table') {
      setOpenFieldPicker(true);
    } else {
      setOpenFieldPicker(false);
      setDisplayType('table');
    }
  };

  const handleSetAxes = (newAxes: { x: string; y: string }) => {
    setAxes(newAxes);
  };

  const handleSetChart = (type: 'chart' | 'table') => {
    setDisplayType(type);
  };

  const handleColumnVisibilityChange = (newVisibleColumns: string[]) => {
    if (onVisibleColumnsChange) {
      onVisibleColumnsChange(newVisibleColumns);
    } else {
      setInternalVisibleColumns(newVisibleColumns);
    }
  };

  const handleSerialNumberToggle = (serialNumber: string) => {
    setSelectedSerialNumbers(prev => {
      if (prev.includes(serialNumber)) {
        return prev.filter(sn => sn !== serialNumber);
      } else {
        return [...prev, serialNumber];
      }
    });
  };

  const filteredColumns = columns.filter(col => visibleColumns.includes(col.id));
  const serialNumbers = data.map((row: any) => row[1])
  const uniqueSerialNumbers = serialNumbers.filter((v, i) => serialNumbers.indexOf(v) === i);
  const uniqueSerialNumbersString = uniqueSerialNumbers.join(', ');

  // Filter data based on selected serial numbers
  const filteredDataBySerial = React.useMemo(() => {
    if (selectedSerialNumbers.length === 0) {
      return data; // Show all data if no serial numbers are selected
    }
    return data.filter((row: any) => selectedSerialNumbers.includes(row[1]));
  }, [data, selectedSerialNumbers]);

  // Create filtered data that matches the filtered columns
  const filteredData = React.useMemo(() => {
    return filteredDataBySerial.map(row => {
      const filteredRow: any[] = [];
      filteredColumns.forEach(col => {
        const columnIndex = columns.findIndex(c => c.id === col.id);
        filteredRow.push(row[columnIndex]);
      });
      return filteredRow;
    });
  }, [filteredDataBySerial, filteredColumns, columns]);

  // Update internal visible columns when columns prop changes
  useEffect(() => {
    setInternalVisibleColumns(columns.map(col => col.id));
  }, [columns]);

  const isValidDate = (str: string) => {
    const date = new Date(str);
    return !isNaN(date.getTime());
  }

  const formatDate = (value: any): string => {
    if (!value) return '';
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
    } catch {
      return value;
    }
  };

  const sortedData = React.useMemo(() => {
    if (!orderBy) return filteredData;

    const column = filteredColumns.find(col => col.id === orderBy);
    if (!column) return filteredData;

    return [...filteredData].sort((a, b) => {
      const columnIndex = filteredColumns.findIndex(col => col.id === orderBy);
      const aValue = a[columnIndex];
      const bValue = b[columnIndex];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      if (aValue === bValue) return 0;

      let comparison = 0;

      if (!isNaN(aValue)) {
        // Numeric comparison
        comparison = Number(aValue) - Number(bValue);
      } else if (isValidDate(aValue)) {
        // Date comparison
        const dateA = new Date(aValue).getTime();
        const dateB = new Date(bValue).getTime();
        comparison = dateA - dateB;
      } else {
        // String comparison
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, order, orderBy, filteredColumns]);

  const row_data = sortedData.map(row => ({
    x: row[filteredColumns.findIndex(col => col.id === axes.x)],
    y: row[filteredColumns.findIndex(col => col.id === axes.y)]
  }));

  return (
    <Box sx={{ width: '100%', mb: 1, overflowY: 'auto', height: '40vh' }}>
      <Box position="sticky" top={0} zIndex={999}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#156082',
          color: '#ffffff',
          py: 0.5,
          px: 1
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
            <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', textAlign: 'left', lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography variant="body2" component="div" sx={{ opacity: 0.8, lineHeight: 1.2 }}>
              {subtitle}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
            <Tooltip title="Al Star">
              <IconButton
                size="small"
                onClick={(event) => {
                  setClickedTable('bugs_table');
                  setClickedThumb('');
                }}
                sx={{
                  backgroundColor: clickedTable === 'bugs_table' ? '#aac9e8' : 'transparent',
                  '&:hover': {
                    backgroundColor: clickedTable === 'bugs_table' ? '#aac9e8' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <PestControlIcon
                  fontSize="small"
                  color={clickedTable === 'bugs_table' ? 'primary' : 'secondary'}
                  sx={{
                    color: clickedTable === 'bugs_table' ? '#1976d2' : undefined
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Knowledge Document">
              <IconButton
                size="small"
                onClick={(event) => {
                  setClickedTable('knowledge_table');
                  setClickedThumb('');
                }}
                sx={{
                  backgroundColor: clickedTable === 'knowledge_table' ? '#aac9e8' : 'transparent',
                  '&:hover': {
                    backgroundColor: clickedTable === 'knowledge_table' ? '#aac9e8' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <MenuBookIcon
                  fontSize="small"
                  color={clickedTable === 'knowledge_table' ? 'primary' : 'secondary'}
                  sx={{
                    color: clickedTable === 'knowledge_table' ? '#1976d2' : undefined
                  }}
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Web Search">
              <IconButton
                size="small"
                onClick={(event) => {
                  setClickedTable('web_search_table');
                  setClickedThumb('');
                }}
                sx={{
                  backgroundColor: clickedTable === 'web_search_table' ? '#aac9e8' : 'transparent',
                  '&:hover': {
                    backgroundColor: clickedTable === 'web_search_table' ? '#aac9e8' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <LanguageIcon
                  fontSize="small"
                  color={clickedTable === 'web_search_table' ? 'primary' : 'secondary'}
                  sx={{
                    color: clickedTable === 'web_search_table' ? '#1976d2' : undefined
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#ededed',
          color: '#000',
          py: 0.5,
          px: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', lineHeight: 1.2, mr: 1 }}>
              {clickedTable === 'bugs_table' ? 'Serial Numbers:' :
                clickedTable == 'knowledge_table' ? 'Insights from Knowledge Book' : 'Insights from Web Search'}
            </Typography>
            {clickedTable === 'bugs_table' && (
              <>
                {uniqueSerialNumbers.map((serialNumber) => (
                  <Box
                    key={serialNumber}
                    component="button"
                    onClick={() => handleSerialNumberToggle(serialNumber)}
                    sx={{
                      backgroundColor: selectedSerialNumbers.includes(serialNumber) ? '#1976d2' : '#f5f5f5',
                      color: selectedSerialNumbers.includes(serialNumber) ? '#ffffff' : '#000000',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: selectedSerialNumbers.includes(serialNumber) ? '#1565c0' : '#e0e0e0',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {serialNumber}
                  </Box>
                ))}
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
            <Tooltip title="Good answer">
              <IconButton
                size="small"
                onClick={(event) => {
                  setClickedThumb('good_answer');
                }}
              >
                <ThumbUp fontSize="small" color={clickedThumb === 'good_answer' ? 'success' : 'secondary'} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Bad answer">
              <IconButton
                size="small"
                onClick={(event) => {
                  setClickedThumb('bad_answer');
                }}
              >
                <ThumbDown fontSize="small" color={clickedThumb === 'bad_answer' ? 'error' : 'secondary'} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {clickedTable === 'bugs_table' && (
        <TableContainer component={Paper}>
          <Table stickyHeader aria-label="sticky table" size="small">
            <TableHead>
              <TableRow>
                {filteredColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    sx={{
                      minWidth: column.minWidth,
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      py: 0.5,
                      px: 1,
                      fontSize: '0.9rem'
                    }}
                    sortDirection={orderBy === column.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                      sx={{ fontSize: '0.9rem' }}
                    >
                      {column.label}
                      {orderBy === column.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length === 0 && (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    textAlign: 'center',
                    padding: '16px'
                  }}>
                  No similar record found from past reports.
                </Typography>)
              }
              {sortedData.map((row, rowIndex) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={rowIndex}>
                  {filteredColumns.map((column, index) => {
                    const value = row[index];
                    let formattedValue = value;

                    return (
                      <TableCell
                        key={column.id}
                        align={column.align || 'left'}
                        sx={{
                          py: 0.25,
                          px: 1,
                          fontSize: '0.9rem',
                          lineHeight: 1.2
                        }}
                      >
                        {formattedValue}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(clickedTable === 'knowledge_table' || clickedTable === 'web_search_table') && isNlgParameterTransmissionLoading && (
        <Box sx={{ p: 0.5, textAlign: 'left' }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {clickedTable === 'knowledge_table' && !isNlgParameterTransmissionLoading && (
        <Box
          sx={{ p: 0.5, textAlign: 'left', fontSize: '0.9rem', lineHeight: 1.2 }}
          dangerouslySetInnerHTML={{
            __html: nlgParameterTransmissionResult?.find((item: any) => item.response_type === 'vector_db')?.response_data || ''
          }}
        />
      )}

      {clickedTable === 'web_search_table' && !isNlgParameterTransmissionLoading && (
        <Box
          sx={{ p: 0.5, textAlign: 'left', fontSize: '0.9rem', lineHeight: 1.2 }}
          dangerouslySetInnerHTML={{
            __html: nlgParameterTransmissionResult?.find((item: any) => item.response_type === 'web_search')?.response_data || ''
          }}
        />
      )}


      <FieldsPicker
        open={openFieldPicker}
        setOpen={setOpenFieldPicker}
        fields={filteredColumns.map(col => col.label)}
        setAxes={handleSetAxes}
        setChart={handleSetChart}
      />

      <ColumnVisibilityPicker
        open={openColumnPicker}
        setOpen={setOpenColumnPicker}
        columns={columns}
        visibleColumns={visibleColumns}
        onVisibilityChange={handleColumnVisibilityChange}
        anchorEl={columnPickerAnchorEl}
      />
    </Box>
  );
};

export default TransmissionTable; 